<template>
    <DsModal
        class="kmn-verification-form"
        :is-open="isOpen"
        :title="isFormEditable ? labels.numberSetup : labels.readOnlyNumberSetup"
        show-close
        @close="$emit('close')"
    >
        <form
            ref="validation-form"
            class="validation-form"
        >
            <section v-if="!isHardRejected" class="heading" :class="{ 'heading-title-only': isApproved }">
                <h2 class="title">
                    {{ i18nFormTitle }}
                </h2>

                <span v-if="!isApproved" class="description">
                    {{ isFormEditable ? labels.description : labels.readOnlyDescription }}
                </span>
            </section>

            <section>
                <KtvSmsVerificationStatusAlert
                    :message="rejectionReason"
                    :rejection-date="rejectionDate"
                    :show-button="isHardRejected"
                    :source="$options.KTV_VERIFICATION_ALERT_SOURCES.KTV_VERIFICATION_FORM"
                    :verification-status="(localSmsAccount||{}).verificationStatus || ''"
                    @action="$emit('cancel')"
                />
            </section>

            <section>
                <h2 class="section-header">
                    {{ labels.useCaseHeader }}
                </h2>

                <DsMultiselect
                    v-model="formData.usageDetails.useCases"
                    bind-value-only
                    class="number-useCase"
                    multiple
                    required
                    searchable
                    :submitted="submitted"
                    :options="useCaseOptions"
                    :placeholder="labels.useCasePlaceholder"
                    :readonly="!isFormEditable"
                />
            </section>

            <section>
                <h2 class="section-header">
                    {{ labels.useCaseSummaryHeader }}
                </h2>

                <DsPopover
                    :is-open="isUseCasesOpen"
                    position="bottom"
                    class="popover-text"
                    @close="isUseCasesOpen = false"
                >
                    <template #reference>
                        <span class="uses-help">{{ labels.needHelp }} </span>

                        <DsLink as="button" data-qa="explain-use-tip" @click="isUseCasesOpen = !isUseCasesOpen">
                            {{ labels.exampleUsesTooltip }}
                        </DsLink>
                    </template>

                    <template #default>
                        <div class="usage-tooltip">
                            <div class="section-header">
                                {{ labels.usagesTooltip.title }}
                            </div>

                            <div class="description">
                                {{ labels.usagesTooltip.description }}
                            </div>

                            <div class="tooltip-subsection-header">
                                <DsIcon name="check-circle" class="tooltip-icon" />

                                <span class="approved-example">{{ labels.approvedExample }}</span>
                            </div>

                            <div class="description">
                                {{ labels.usagesTooltip.approvedDescription }}
                            </div>

                            <div class="tooltip-subsection-header">
                                <DsIcon name="x-circle" class="tooltip-icon" />

                                <span class="rejected-example">{{ labels.rejectedExample }}</span>
                            </div>

                            <div class="tooltip-example-subheader">
                                {{ labels.usagesTooltip.rejectedExample }}
                            </div>

                            <div class="description">
                                {{ labels.usagesTooltip.rejectedDescription }}
                            </div>
                        </div>
                    </template>
                </DsPopover>

                <DsTextAreaField
                    v-model="formData.usageDetails.useCaseSummary"
                    data-qa="intended-use-summary"
                    class="example-use-message"
                    :submitted="submitted"
                    :label="labels.useCaseExplanation"
                    :maxlength="$options.MAX_USE_CASE_COUNT"
                    auto-grow
                    required
                    :readonly="!isFormEditable"
                >
                    <template #help>
                        <span class="character-count">{{ formData.usageDetails.useCaseSummary.length }}/{{ $options.MAX_USE_CASE_COUNT }}</span>
                    </template>
                </DsTextAreaField>
            </section>

            <section>
                <h2 class="section-header">
                    {{ labels.sampleMessageFooter }}
                </h2>

                <DsPopover
                    :is-open="isSampleMessageTooltipOpen"
                    position="bottom"
                    class="popover-text"
                    @close="isSampleMessageTooltipOpen = false"
                >
                    <template #reference>
                        <span class="uses-help">{{ labels.needHelp }} </span>

                        <DsLink as="button" data-qa="explain-use-tip" @click="isSampleMessageTooltipOpen = !isSampleMessageTooltipOpen">
                            {{ labels.sampleMessagesTooltip }}
                        </DsLink>
                    </template>

                    <template #default>
                        <div class="usage-tooltip">
                            <div class="section-header">
                                {{ labels.sampleMessages.title }}
                            </div>

                            <div class="tooltip-subsection-header">
                                <DsIcon name="check-circle" class="tooltip-icon" />

                                <span class="approved-example">{{ labels.approvedExample }}</span>
                            </div>

                            <div class="description">
                                {{ labels.sampleMessages.approvedExample }}
                            </div>

                            <div class="tooltip-subsection-header">
                                <DsIcon name="x-circle" class="tooltip-icon" />

                                <span class="rejected-example">{{ labels.rejectedExample }}</span>
                            </div>

                            <div class="tooltip-example-subheader">
                                {{ labels.sampleMessages.rejectedExample }}
                            </div>

                            <div class="description">
                                {{ labels.sampleMessages.rejectedExampleDescription }}
                            </div>
                        </div>
                    </template>
                </DsPopover>

                <DsTextAreaField
                    v-model="formData.usageDetails.sampleMessage"
                    data-qa="sample-message"
                    class="sample-message"
                    :submitted="submitted"
                    :label="labels.sampleMessages.placeholder"
                    :maxlength="$options.MAX_MESSAGE_COUNT"
                    auto-grow
                    required
                    :readonly="!isFormEditable"
                >
                    <template #help>
                        <span class="character-count">{{ formData.usageDetails.sampleMessage.length }}/{{ $options.MAX_MESSAGE_COUNT }}</span>
                    </template>
                </DsTextAreaField>
            </section>

            <DsInlineAlert
                v-if="isFormEditable"
                class="prohibited-content-alert"
                leading-icon
                type="critical"
            >
                <span>
                    {{ labels.smsContentAlert.header }}
                </span>

                <ul class="prohibited-types">
                    <li v-for="type in labels.smsContentAlert.types" :key="type">
                        {{ type }}
                    </li>
                </ul>

                <span>
                    {{ labels.smsContentAlert.footer }}

                    <DsLink
                        trailing-icon="external-link"
                        href="https://keap.com/legal/sms-compliance"
                        external
                    >
                        {{ labels.smsContentAlert.linkText }}
                    </DsLink>
                </span>
            </DsInlineAlert>

            <section class="separator">
                <h2 class="section-header">
                    {{ labels.optIn.subheading }}
                </h2>

                <DsMultiselect
                    v-model="formData.usageDetails.optInType"
                    data-qa="voice-text-opt-in-type"
                    bind-value-only
                    class="number-opt-in-value"
                    required
                    searchable
                    :submitted="submitted"
                    :options="optInTypes"
                    :placeholder="labels.optIn.label"
                    :readonly="!isFormEditable"
                />

                <div
                    v-if="isKmnBrandedOptInEnabled"
                    data-qa="opt-in-image-url-section"
                >
                    <h2 class="section-header">
                        {{ labels.optInImageUrlTitle }}
                    </h2>

                    <DsPopover
                        :is-open="isOptInImageUrlPopoverOpen"
                        class="popover-text"
                        data-qa="opt-in-image-url-popover"
                        position="bottom"
                        @close="isOptInImageUrlPopoverOpen = false"
                    >
                        <template #reference>
                            <span class="uses-help">{{ labels.needHelp }} </span>

                            <DsLink
                                as="button"
                                data-qa="opt-in-image-url-popover-trigger"
                                @click="isOptInImageUrlPopoverOpen = !isOptInImageUrlPopoverOpen"
                            >
                                {{ labels.optInImageUrlPopover.trigger }}
                            </DsLink>
                        </template>

                        <template #default>
                            <div class="usage-tooltip">
                                <div class="section-header">
                                    {{ labels.optInImageUrlPopover.title }}
                                </div>

                                <div
                                    v-for="{ title, instruction } in labels.optInImageUrlPopover.methods"
                                    :key="title"
                                    class="opt-in-image-url-popover-item"
                                    data-qa="opt-in-image-url-popover-item"
                                >
                                    <strong>{{ title }}:</strong> {{ instruction }}
                                </div>

                                <span data-qa="opt-in-image-url-instructions">
                                    {{ labels.brandedOptInGuidance }}
                                    <DsLink
                                        external
                                        trailing-icon="external-link"
                                        :href="$options.TOLL_FREE_MESSAGE_VERIFICATION_URL"
                                        data-qa="opt-in-image-url-link"
                                    >
                                        {{ labels.brandedOptInLinkText }}
                                    </DsLink>
                                </span>
                            </div>
                        </template>
                    </DsPopover>

                    <DsInputField
                        v-model="formData.usageDetails.optInImageUrl"
                        class="opt-in-image-url-input"
                        data-qa="opt-in-image-url-input"
                        required
                        :label="labels.optInImageUrlInput"
                        :submitted="submitted"
                        :invalid="!isOptInImageUrlValid"
                        :readonly="!isFormEditable"
                    >
                        <template #help>
                            <span class="opt-in-image-url-help" data-qa="opt-in-image-url-help">
                                {{ labels.validUrlHelp }}
                            </span>
                        </template>

                        <template #error>
                            <small v-if="!isOptInImageUrlValid" data-qa="opt-in-image-url-error">
                                {{ labels.validUrlHelp }}
                            </small>
                        </template>
                    </DsInputField>
                </div>

                <h2 class="section-header">
                    {{ labels.numberOfTextsLabel }}
                </h2>

                <ds-multiselect
                    v-model="formData.usageDetails.messageVolume"
                    bind-value-only
                    class="number-of-texts"
                    :submitted="submitted"
                    :options="numberOfTexts"
                    :placeholder="labels.numberOfTextsPlaceholder"
                    required
                    :readonly="!isFormEditable"
                />
            </section>

            <section class="separator">
                <h2 class="section-subheader">
                    {{ labels.general }}
                </h2>

                <div class="input-group">
                    <ds-input-field
                        v-model="formData.generalDetails.firstName"
                        class="name-input"
                        data-qa="first-name"
                        :submitted="submitted"
                        :label="labels.firstName"
                        required
                        :readonly="!isFormEditable"
                    />

                    <ds-input-field
                        v-model="formData.generalDetails.lastName"
                        class="name-input"
                        data-qa="last-name"
                        :submitted="submitted"
                        :label="labels.lastName"
                        required
                        :readonly="!isFormEditable"
                    />
                </div>

                <ds-input-field
                    v-model="formData.generalDetails.companyName"
                    class="company-name"
                    :submitted="submitted"
                    :label="labels.companyName"
                    required
                    :readonly="!isFormEditable"
                />

                <ds-input-field
                    v-model="formData.generalDetails.website"
                    class="company-website"
                    required
                    :label="labels.companyWebsite"
                    :submitted="submitted"
                    :invalid="!isWebsiteValid"
                    :readonly="!isFormEditable"
                >
                    <template #help>
                        <span class="website-help">
                            {{ labels.validUrlHelp }}
                        </span>
                    </template>
                    <template #error>
                        <small v-if="!isWebsiteValid" data-qa="website-error">
                            {{ labels.validUrlHelp }}
                        </small>
                    </template>
                </ds-input-field>
            </section>

            <section class="separator">
                <h2 class="section-subheader">
                    {{ labels.phoneEmail }}
                </h2>

                <ds-input-field
                    v-model="formData.contactInformation.emailAddress"
                    class="email-input"
                    required
                    :label="labels.email"
                    :submitted="submitted"
                    :invalid="!isEmailValid"
                    :readonly="!isFormEditable"
                >
                    <template #error>
                        <small v-if="!isEmailValid" data-qa="email-error">
                            {{ labels.emailError }}
                        </small>
                    </template>
                </ds-input-field>

                <ds-input-field
                    v-model="formData.contactInformation.phoneNumber"
                    class="phone-input"
                    :submitted="submitted"
                    :label="labels.phone"
                    :invalid="!isPhoneNumberValid"
                    required
                    :readonly="!isFormEditable"
                >
                    <template #error>
                        <small v-if="!isPhoneNumberValid" data-qa="phone-error">
                            {{ labels.phoneError }}
                        </small>
                    </template>
                </ds-input-field>

                <ds-input-field
                    v-model="localSmsAccount.phoneNumber"
                    class="kmn-input"
                    :label="labels.marketingNumber"
                    readonly
                    required
                />
            </section>

            <section>
                <h2 class="section-subheader">
                    {{ labels.address }}
                </h2>

                <ds-input-field
                    v-model="formData.companyAddress.address1"
                    class="company-address1"
                    :submitted="submitted"
                    :label="labels.companyAddress1"
                    required
                    :readonly="!isFormEditable"
                />

                <ds-input-field
                    v-model="formData.companyAddress.address2"
                    class="company-address2"
                    :label="labels.companyAddress2"
                    :readonly="!isFormEditable"
                />

                <DsMultiselect
                    v-model="formData.companyAddress.country"
                    bind-value-only
                    data-qa="voice-text-company-country"
                    class="country-input"
                    :placeholder="labels.country"
                    :options="countries"
                    :submitted="submitted"
                    required
                    :readonly="!isFormEditable"
                />

                <div class="input-group">
                    <ds-input-field
                        v-model="formData.companyAddress.locality"
                        class="small-input city-input"
                        :submitted="submitted"
                        :label="labels.city"
                        required
                        :readonly="!isFormEditable"
                    />

                    <ds-multiselect
                        v-model="formData.companyAddress.region"
                        bind-value-only
                        class="small-input region-input"
                        :placeholder="labels.region"
                        :options="regionOptions"
                        :submitted="submitted"
                        required
                        :readonly="!isFormEditable"
                    />

                    <ds-input-field
                        v-model="formData.companyAddress.postalCode"
                        class="zipcode-input"
                        :label="labels.zipcode"
                        :submitted="submitted"
                        :invalid="!isZipcodeValid"
                        required
                        :readonly="!isFormEditable"
                    >
                        <template #error>
                            <small v-if="!isZipcodeValid" data-qa="zip-error">
                                {{ labels.zipcodeError }}
                            </small>
                        </template>
                    </ds-input-field>
                </div>
            </section>

            <div
                v-if="hasValidationError || isSubmittedFormDataUnchanged"
                class="error-container"
                data-qa="error-inline-alert"
            >
                <DsInlineAlert leading-icon type="critical">
                    <template #default>
                        {{ isSubmittedFormDataUnchanged ? labels.errors.formDataUnchanged : labels.errors.formValidationError }}
                    </template>
                </DsInlineAlert>
            </div>

            <div class="controls">
                <template v-if="isFormEditable">
                    <DsFilledButton
                        class="finish-setup"
                        data-qa="text-and-voice-verification-form-submit"
                        :loading="loading"
                        @click="save"
                    >
                        {{ isFormResubmittable ? labels.editAndResubmit : labels.finishSetup }}
                    </DsFilledButton>

                    <DsOutlineButton
                        class="finish-close"
                        data-qa="text-and-voice-verification-form-cancel"
                        @click="$emit('close')"
                    >
                        {{ labels.cancel }}
                    </DsOutlineButton>
                </template>

                <template v-else>
                    <DsOutlineButton
                        class="finish-close"
                        data-qa="text-and-voice-verification-form-close"
                        @click="$emit('close')"
                    >
                        {{ labels.close }}
                    </DsOutlineButton>
                </template>
            </div>
        </form>
    </DsModal>
</template>

<script>
import axios from 'axios';
import KtvSmsVerificationStatusAlert, { KTV_VERIFICATION_ALERT_SOURCES } from '@/components/automatedSms/KtvSmsVerificationStatusAlert';
import {
    isValidNorthAmericaPhoneNumber, toE164NorthAmericaPhoneFormat,
} from '@/components/broadcast/utils/sms-utils';
import {
    CONSENT_ACQUISITION_KIND,
    KEAP_PROVIDED_OPT_IN_IMAGE_URL,
    KMN_VERIFICATION_STATUS_TYPE,
    TOLL_FREE_MESSAGE_VERIFICATION_URL,
} from '@/components/broadcast/constants/smsBroadcast.constants';
import {
    getStateMapForCountry,
} from '@/api/broadcasts/emailBroadcast';

import moment from 'moment/moment';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import sentry from '@/analytics/sentry';


const KMN_VALIDATION_SOURCE = CONSENT_ACQUISITION_KIND.FORM;
const ERROR_MESSAGE = 'For some reason your form didn’t save. Try refreshing the page.';
const CLOSE_LABEL = 'Ok, got it';
const ZIPCODE_REGEX = /(^\d{5}$)|(^\d{5}-\d{4}$)/;
const PHONE_NUMBER_REGEX = /^[\d ()+-]+$/;
const EMAIL_ADDRESS_PATTERN = /^\S+@\S+\.\S+$/;
// This only makes sure the website begins with protocol, has TLD, has no spaces and nothing else
const WEBSITE_REGEX = /^(https?):\/\/(\S)+\.(\S)+$/i;

const MAX_USE_CASE_COUNT = 500;
const MAX_MESSAGE_COUNT = 1000;

const VERIFICATION_READONLY_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.APPROVED,
    KMN_VERIFICATION_STATUS_TYPE.PENDING_SUBMISSION,
    KMN_VERIFICATION_STATUS_TYPE.PENDING_VERIFICATION,
    KMN_VERIFICATION_STATUS_TYPE.REJECTED,
];

const VERIFICATION_RESUBMITTABLE_TYPES = [
    KMN_VERIFICATION_STATUS_TYPE.EXPIRED,
    KMN_VERIFICATION_STATUS_TYPE.RESUBMISSION,
];

const countryCodeIsoAlpha2ToIsoAlpha3 = (code) => ({ CA: 'CAN', US: 'USA' }[code]);

export default {
    name: 'KmnVerificationForm',

    TOLL_FREE_MESSAGE_VERIFICATION_URL,
    KTV_VERIFICATION_ALERT_SOURCES,
    MAX_USE_CASE_COUNT,
    MAX_MESSAGE_COUNT,

    components: { KtvSmsVerificationStatusAlert },

    props: {
        isOpen: Boolean,
        kmnAccount: {
            type: Object,
            required: true,
        },
        daysToResubmit: {
            type: Number,
            default: 7,
        },
        isKmnBrandedOptInEnabled: Boolean,
        kmnPhoneVerificationEnabled: Boolean,
        source: String,
        readOnly: Boolean,
    },

    data() {
        return {
            formData: {
                generalDetails: {
                    companyName: '',
                    firstName: '',
                    lastName: '',
                    website: '',
                },
                contactInformation: {
                    emailAddress: '',
                    phoneNumber: '',
                },
                companyAddress: {
                    address1: '',
                    address2: '',
                    country: 'US',
                    locality: '',
                    postalCode: '',
                    region: '',
                },
                usageDetails: {
                    messageVolume: '',
                    optInType: '',
                    optInImageUrl: '',
                    sampleMessage: '',
                    useCaseSummary: '',
                    useCases: [],
                },
            },
            hasValidationError: false,
            loading: false,
            region: {},
            rejectionDate: '',
            rejectionReason: '',
            submitted: false,
            isSubmittedFormDataUnchanged: false,
            isUseCasesOpen: false,
            isOptInImageUrlPopoverOpen: false,
            isSampleMessageTooltipOpen: false,
            localSmsAccount: this.kmnAccount,
            numberOfTexts: [
                { value: 10, label: '10' },
                { value: 100, label: '100' },
                { value: 1000, label: '1,000' },
                { value: 10000, label: '10,000' },
                { value: 100000, label: '100,000' },
                { value: 250000, label: '250,000' },
                { value: 500000, label: '500,000' },
                { value: 750000, label: '750,000' },
                { value: 1000000, label: '1,000,000' },
                { value: 5000000, label: '5,000,000' },
                { value: 10000000, label: '10,000,000' },
            ],
            regionOptions: [],
            labels: {
                address: 'Address',
                approvedExample: 'Approved Example:',
                city: 'City',
                companyAddress1: 'Company address 1',
                companyAddress2: 'Company address 2',
                companyName: 'Company name',
                companyWebsite: 'Company website',
                countries: {
                    us: 'United States',
                },
                country: 'Country',
                customerOutreach: 'Customer outreach',
                description: 'Due to new regulations, we need extra details about your business. Please accurately answer each question. Incomplete or inaccurate information may lead to the rejection of your application.',
                readOnlyDescription: 'Your Keap Marketing Number verification has been successfully submitted and is pending review with our carriers.',
                email: 'Email',
                emailError: 'Please enter a valid email address.',
                sampleMessagesTooltip: 'See examples of approved uses.',
                exampleUsesTooltip: 'See examples of approved messages.',
                finishSetup: 'Submit',
                cancel: 'Cancel',
                editAndResubmit: 'Resubmit form',
                close: 'Close',
                firstName: 'First name',
                general: 'General',
                lastName: 'Last name',
                leadNurture: 'Lead nurture',
                marketingNumber: 'Keap Marketing Number',
                needHelp: 'Need help?',
                validUrlHelp: 'URLs must start with http:// or https://, should not contain spaces, and must include a valid domain extension.',
                optInImageUrlTitle: 'Provide a link to a web form, image, or document that demonstrates your primary opt-in method for text messaging.',
                optInImageUrlInput: 'Opt-in documentation URL',
                optInImageUrlPopoverTrigger: 'See examples of proper opt-in documentation.',
                optInImageUrlPopover: {
                    trigger: 'See examples of proper opt-in documentation.',
                    title: 'Opt-in methods:',
                    methods: [{
                        title: 'Verbal',
                        instruction: 'document must include the script and process you use to obtain verbal consent',
                    }, {
                        title: 'Web form',
                        instruction: 'provide the link or a screenshot of the website opt-in page',
                    }, {
                        title: 'Paper form',
                        instruction: 'provide a digital or scanned copy of the paper form',
                    }, {
                        title: 'Via text',
                        instruction: 'screenshot or image of your opt-in/opt-out text keywords',
                    }, {
                        title: 'Mobile QR code',
                        instruction: 'include a document with the QR Code',
                    }],
                },
                notifications: 'Notifications',
                numberOfTextsLabel: 'How many texts will you send in a month?',
                numberOfTextsPlaceholder: 'Number of monthly texts',
                numberSetup: 'Keap Marketing Number setup',
                readOnlyNumberSetup: 'Keap Marketing Number Verification',
                titleApproved: 'Verification approved!',
                optIn: {
                    label: 'Select opt-in method',
                    mobileCode: 'Mobile QR code',
                    paperForm: 'Paper form',
                    subheading: 'What is your primary method to opt-in your customers for text messaging?',
                    verbal: 'Verbal',
                    viaText: 'Via text',
                    webForm: 'Web form',
                },
                brandedOptInGuidance: 'For more guidance on opt-in documentation, see',
                brandedOptInLinkText: 'this help article',
                phone: 'Phone',
                phoneEmail: 'Phone and email',
                phoneError: 'Please enter a valid phone number.',
                promotions: 'Promotions',
                region: 'State/Territory',
                rejectedExample: 'Rejected Example:',
                sampleMessageFooter: 'Provide an example of a text message your customer will receive.',
                sampleMessageLabel: 'Sample message',
                smsContentAlert: {
                    footer: 'For a complete list of content restrictions, please review our ',
                    header: 'Any content involving the below is prohibited and may lead to the suspension of your Keap Marketing Number:',
                    linkText: 'Acceptable Use Policy',
                    types: ['Controlled substances, including cannabis and CBD', 'Alcohol', 'Firearms', 'Tobacco', 'Sexual or sexually suggestive content', 'Hate and hatespeech'],
                },
                title: 'We need to verify some info',
                readOnlyTitle: 'Verification submitted!',
                sampleMessages: {
                    approvedExample: '"Thank you for being a loyal customer of John’s Coffee Shop. Enjoy 10% off your next purchase. Reply STOP to opt out." This should be an example of a SMS message the customer will be receiving.',
                    placeholder: 'Example message(s)',
                    rejectedExample: '"Your appointment is today at 10:00 AM."',
                    rejectedExampleDescription: 'This should match the intended uses that you provided (i.e. Promotional offers).',
                    title: 'Example messages',
                },
                usagesTooltip: {
                    approvedDescription: 'This number is used to send out promotional offers and coupons to the customers of John’s Coffee Shop',
                    approvedExample: 'Promotional offers',
                    description: 'Provide clear descriptions of your intended uses that match the examples you have provided. The more detail the better.',
                    rejectedDescription: 'You must describe the type of marketing message the customer will be receiving from the business.',
                    rejectedExample: 'Marketing',
                    title: 'Intended uses',
                },
                useCases: {
                    accountNotifications: 'Account Notifications',
                    charityNonprofit: 'Charity Nonprofit',
                    customerCare: 'Customer Care',
                    deliveryNotifications: 'Delivery Notifications',
                    events: 'Events',
                    fraudAlertMessaging: 'Fraud Alert Messaging',
                    higherEducation: 'Higher Education',
                    k12: 'K12',
                    marketing: 'Marketing',
                    politicalElectionCampaigns: 'Political Election Campaigns',
                    pollingAndVotingNonPolitical: 'Polling And Voting Non Political',
                    publicServiceAnnouncement: 'Public Service Announcement',
                    securityAlert: 'Security Alert',
                    twoFactorAuthentication: 'Two Factor Authentication',
                },
                useCaseExplanation: 'Explain your intended use(s)',
                useCaseHeader: 'How do you plan to use your Keap Marketing Number?',
                useCasePlaceholder: 'Select examples',
                verificationWarningAlert: 'While your marketing number is being verified, you may be subject to restrictions on the number of messages you can send per day. Please note, if the form isn\'t completed or contains inaccurate information, your number could be suspended.',
                useCaseSummaryHeader: 'Provide details on how you’ll use your marketing number for the purposes you’ve selected.',
                verificationNotExpired: 'Your verification form has been rejected but is eligible for resubmission. You may be subject to restrictions on the number of messages you can send until your form is resubmitted and fully verified. Be aware if you do not resubmit your verification ',
                verificationNotExpired2: ', this process will restart and may take an additional 6 weeks.',
                verificationExpired: 'Your verification form has been rejected but is eligible for resubmission.  While your number is being verified, you will be unable to send text messages. To get full access, please resubmit your form. This process may take an additional 6 weeks.',
                zipcode: 'Postal code',
                zipcodeError: 'Please enter a valid postal code.',
                errors: {
                    formDataUnchanged: 'We did not see that you made any changes. Please review and make changes before resubmitting the form.',
                    formValidationError: 'Some of the data you entered may be missing or invalid.  Please review and resubmit.',
                },
            },
        };
    },

    created() {
        this.load();
    },

    watch: {
        'formData.companyAddress.country': async function(newVal, oldVal) {
            if (newVal !== oldVal) {
                this.formData.companyAddress.region = '';
                await this.loadRegionOptions();
            }
        },

        formData: {
            handler() { this.handleFormDataChanged(); },
            deep: true,
            immediate: true,
        },
    },

    computed: {
        daysRemainingForSubmission() {
            const { daysToResubmit, rejectionDate } = this;

            if (!rejectionDate) {
                return 0;
            }
            const dateToday = new Date();
            const daysElapsed = Math.ceil(moment.duration(moment(dateToday).diff(rejectionDate)).asDays());
            const result = daysToResubmit - daysElapsed;

            return typeof result === 'number' ? result + 1 : 0;
        },

        countries() {
            return [{ value: 'US', label: this.labels.countries.us }];
        },

        optInTypes() {
            return [
                { value: 'VERBAL', label: this.labels.optIn.verbal },
                { value: 'WEB_FORM', label: this.labels.optIn.webForm },
                { value: 'PAPER_FORM', label: this.labels.optIn.paperForm },
                { value: 'VIA_TEXT', label: this.labels.optIn.viaText },
                { value: 'MOBILE_QR_CODE', label: this.labels.optIn.mobileCode },
            ];
        },

        useCaseOptions() {
            const { useCases } = this.labels;

            return [
                { value: 'ACCOUNT_NOTIFICATIONS', label: useCases.accountNotifications },
                { value: 'CHARITY_NONPROFIT', label: useCases.charityNonprofit },
                { value: 'CUSTOMER_CARE', label: useCases.customerCare },
                { value: 'DELIVERY_NOTIFICATIONS', label: useCases.deliveryNotifications },
                { value: 'EVENTS', label: useCases.events },
                { value: 'FRAUD_ALERT_MESSAGING', label: useCases.fraudAlertMessaging },
                { value: 'HIGHER_EDUCATION', label: useCases.higherEducation },
                { value: 'K12', label: useCases.k12 },
                { value: 'MARKETING', label: useCases.marketing },
                { value: 'POLITICAL_ELECTION_CAMPAIGNS', label: useCases.politicalElectionCampaigns },
                { value: 'POLLING_AND_VOTING_NON_POLITICAL', label: useCases.pollingAndVotingNonPolitical },
                { value: 'PUBLIC_SERVICE_ANNOUNCEMENT', label: useCases.publicServiceAnnouncement },
                { value: 'SECURITY_ALERT', label: useCases.securityAlert },
                { value: 'TWO_FACTOR_AUTHENTICATION', label: useCases.twoFactorAuthentication },
            ];
        },

        isEmailValid() {
            const { formData: { contactInformation: { emailAddress } } } = this;

            if (!emailAddress) return false;

            return EMAIL_ADDRESS_PATTERN.test(emailAddress);
        },

        isWebsiteValid() {
            const { formData: { generalDetails: { website } } } = this;

            if (!website) return false;

            return WEBSITE_REGEX.test(website);
        },

        isOptInImageUrlValid() {
            const { formData: { usageDetails: { optInImageUrl } } } = this;

            if (!optInImageUrl) return false;

            return WEBSITE_REGEX.test(optInImageUrl);
        },

        isPhoneNumberValid() {
            const { formData: { contactInformation: { phoneNumber } } } = this;

            if (!phoneNumber) return false;

            if (!PHONE_NUMBER_REGEX.test(phoneNumber)) return false;

            return isValidNorthAmericaPhoneNumber(phoneNumber);
        },

        isZipcodeValid() {
            const { formData: { companyAddress: { postalCode: zip } } } = this;

            if (!zip) return false;

            return ZIPCODE_REGEX.test(zip);
        },

        isValidForm() {
            const {
                isKmnBrandedOptInEnabled,
                isEmailValid,
                isWebsiteValid,
                isOptInImageUrlValid,
                isPhoneNumberValid,
                isZipcodeValid,
                formData: {
                    companyAddress,
                    generalDetails,
                    usageDetails,
                },
            } = this;

            if (isKmnBrandedOptInEnabled && !isOptInImageUrlValid) {
                return false;
            }

            return !!+usageDetails.messageVolume
                && !!companyAddress.address1
                && !!companyAddress.country
                && !!companyAddress.locality
                && !!companyAddress.postalCode
                && !!companyAddress.region
                && !!generalDetails.companyName
                && !!generalDetails.firstName
                && !!generalDetails.lastName
                && !!generalDetails.website
                && !!usageDetails.optInType
                && usageDetails.sampleMessage.length <= MAX_MESSAGE_COUNT
                && usageDetails.sampleMessage.length > 0
                && usageDetails.useCaseSummary.length <= MAX_USE_CASE_COUNT
                && usageDetails.useCaseSummary.length > 0
                && usageDetails.useCases.length > 0
                && isEmailValid
                && isPhoneNumberValid
                && isWebsiteValid
                && isZipcodeValid;
        },

        isFormEditable() {
            const { verificationStatus } = this.localSmsAccount;

            return !VERIFICATION_READONLY_TYPES.includes(verificationStatus);
        },

        isFormResubmittable() {
            const { verificationStatus } = this.localSmsAccount;

            return this.isFormEditable && VERIFICATION_RESUBMITTABLE_TYPES.includes(verificationStatus);
        },

        isApproved() {
            return this.localSmsAccount.verificationStatus === KMN_VERIFICATION_STATUS_TYPE.APPROVED;
        },

        isHardRejected() {
            return this.localSmsAccount.verificationStatus === KMN_VERIFICATION_STATUS_TYPE.REJECTED;
        },

        i18nFormTitle() {
            if (this.isApproved) {
                return this.labels.titleApproved;
            }

            return this.isFormEditable ? this.labels.title : this.labels.readOnlyTitle;
        },
    },

    methods: {
        async loadSmsAccount() {
            this.localSmsAccount = await axios.get('/app/automatedSmsFunnel/getAccount') || {};

            return this.localSmsAccount;
        },

        async load() {
            this.loadI18nLabels();

            await Promise.all([
                this.loadProfileData(),
                this.loadKmnData(),
            ]);
        },

        async loadKmnData() {
            if (!this.localSmsAccount.phoneNumber) {
                return;
            }

            try {
                const { data } = await axios.get(`/app/automatedSmsFunnel/v1/verification/${this.localSmsAccount.phoneNumber}`);

                if (!data) {
                    return;
                }

                const {
                    businessAddr1,
                    businessAddr2,
                    businessContactEmail,
                    businessContactFirstName,
                    businessContactLastName,
                    businessContactPhone,
                    city,
                    companyName,
                    corporateWebsite,
                    messageVolume,
                    optInType,
                    sampleMessage,
                    state,
                    useCaseCategories,
                    useCaseSummary,
                    zip,
                    country,
                    rejectionDate,
                    errorDescription,
                    optInImageUrls,
                } = data;

                const useCases = (Array.isArray(useCaseCategories) ? useCaseCategories : [])
                    .filter(((c) => this.useCaseOptions.map(({ value }) => value).includes(c)));

                // USAGE DETAILS
                if (useCases.length) this.formData.usageDetails.useCases = useCases;
                if (useCaseSummary) this.formData.usageDetails.useCaseSummary = useCaseSummary;
                if (sampleMessage) this.formData.usageDetails.sampleMessage = sampleMessage;
                if (optInType) this.formData.usageDetails.optInType = optInType;
                if (messageVolume) this.formData.usageDetails.messageVolume = +messageVolume;

                if (this.isKmnBrandedOptInEnabled && optInImageUrls && optInImageUrls !== KEAP_PROVIDED_OPT_IN_IMAGE_URL) {
                    this.formData.usageDetails.optInImageUrl = optInImageUrls;
                }

                // PHONE AND EMAIL
                if (businessContactEmail) this.formData.contactInformation.emailAddress = businessContactEmail;
                if (businessContactPhone) this.formData.contactInformation.phoneNumber = businessContactPhone;

                // GENERAL
                if (businessContactFirstName) this.formData.generalDetails.firstName = businessContactFirstName;
                if (businessContactLastName) this.formData.generalDetails.lastName = businessContactLastName;
                if (companyName) this.formData.generalDetails.companyName = companyName;
                if (corporateWebsite) this.formData.generalDetails.website = corporateWebsite;
                if (rejectionDate) this.rejectionDate = rejectionDate;
                if (errorDescription) this.rejectionReason = errorDescription;

                // ADDRESS
                if (country) {
                    this.formData.companyAddress.country = country;

                    await this.loadRegionOptions();

                    if (this.regionOptions.length && state) {
                        this.formData.companyAddress.region = this.findRegionInGlobalRegionOptions(state);
                    }
                }

                if (businessAddr1) this.formData.companyAddress.address1 = businessAddr1;
                if (businessAddr1 || businessAddr2) this.formData.companyAddress.address2 = businessAddr2 || '';
                if (city) this.formData.companyAddress.locality = city;
                if (zip) this.formData.companyAddress.postalCode = zip;

                this.formDataAsFetched = cloneDeep(this.formData);
            } catch (e) {
                sentry.captureException(e);
                console.error('Error loading KMN data', e);
            }
        },

        async loadProfileData() {
            const { data } = await axios.get('/app/businessProfile');

            Object.entries(data.stateOptions).forEach((state) => {
                this.regionOptions.push({ label: state[0], value: state[1] });
            });
        },

        loadI18nLabels() {
            if (window.keapI18nMap !== undefined) {
                Object.assign(this.labels, {
                    numberSetup: window.keapI18nMap['funnel.campaign.communications.kmn.verification.number.setup'],
                    readOnlyNumberSetup: window.keapI18nMap['funnel.campaign.communications.kmn.verification.readOnlyNumberSetup'],
                    title: window.keapI18nMap['funnel.campaign.communications.kmn.verification.title'],
                    readOnlyTitle: window.keapI18nMap['funnel.campaign.communications.kmn.verification.readOnlyTitle'],
                    description: window.keapI18nMap['funnel.campaign.communications.kmn.verification.description'],
                    readOnlyDescription: window.keapI18nMap['funnel.campaign.communications.kmn.verification.readOnlyDescription'],
                    marketingNumber: window.keapI18nMap['funnel.campaign.communications.kmn.verification.marketing.number'],
                    sampleMessageLabel: window.keapI18nMap['funnel.campaign.communications.kmn.verification.sample.message.label'],
                    firstName: window.keapI18nMap['funnel.campaign.communications.kmn.verification.first.name'],
                    lastName: window.keapI18nMap['funnel.campaign.communications.kmn.verification.last.name'],
                    companyName: window.keapI18nMap['funnel.campaign.communications.kmn.verification.company.name'],
                    companyWebsite: window.keapI18nMap['funnel.campaign.communications.kmn.verification.company.website'],
                    general: window.keapI18nMap['funnel.campaign.communications.kmn.verification.general'],
                    phoneEmail: window.keapI18nMap['funnel.campaign.communications.kmn.verification.phone.email'],
                    email: window.keapI18nMap['funnel.campaign.communications.kmn.verification.email'],
                    phone: window.keapI18nMap['funnel.campaign.communications.kmn.verification.phone'],
                    address: window.keapI18nMap['funnel.campaign.communications.kmn.verification.address'],
                    companyAddress1: window.keapI18nMap['funnel.campaign.communications.kmn.verification.company.address1'],
                    companyAddress2: window.keapI18nMap['funnel.campaign.communications.kmn.verification.company.address2'],
                    city: window.keapI18nMap['funnel.campaign.communications.kmn.verification.city'],
                    state: window.keapI18nMap['funnel.campaign.communications.kmn.verification.state'],
                    zipcode: window.keapI18nMap['funnel.campaign.communications.kmn.verification.zipcode'],
                    useCasePlaceholder: window.keapI18nMap['funnel.campaign.communications.kmn.verification.useCase.placeholder'],
                    notifications: window.keapI18nMap['funnel.campaign.communications.kmn.verification.notifications'],
                    customerOutreach: window.keapI18nMap['funnel.campaign.communications.kmn.verification.customer.outreach'],
                    promotions: window.keapI18nMap['funnel.campaign.communications.kmn.verification.promotions'],
                    leadNurture: window.keapI18nMap['funnel.campaign.communications.kmn.verification.lead.nurture'],
                    numberOfTextsLabel: window.keapI18nMap['funnel.campaign.communications.kmn.verification.number.of.texts.placeholder'],
                    sampleMessageFooter: window.keapI18nMap['funnel.campaign.communications.kmn.verification.sample.message.footer'],
                    finishSetup: window.keapI18nMap['funnel.campaign.communications.kmn.verification.finish.setup'],
                    emailError: window.keapI18nMap['funnel.campaign.communications.kmn.verification.email.error'],
                    phoneError: window.keapI18nMap['funnel.campaign.communications.kmn.verification.phone.error'],
                    zipcodeError: window.keapI18nMap['funnel.campaign.communications.kmn.verification.zipcode.error'],
                    resubmitForm: 'Edit and resubmit form',
                });
            }
        },

        findRegionInGlobalRegionOptions(region) {
            return this.regionOptions.find((r) => r.label === region)?.label || '';
        },

        async loadRegionOptions() {
            const { formData: { companyAddress: { country: countryCodeIsoAlpha2 } } } = this;

            if (countryCodeIsoAlpha2) {
                return;
            }

            const country = countryCodeIsoAlpha2ToIsoAlpha3(countryCodeIsoAlpha2);

            const regionOptions = await getStateMapForCountry({ country });

            this.regionOptions = regionOptions.map(({ label }) => ({ value: label, label }));
        },

        hasFormDataChangedSinceFetched() {
            return !isEqual(this.formDataAsFetched, this.formData);
        },

        handleFormDataChanged() {
            if (this.submitted && this.isFormResubmittable && this.isSubmittedFormDataUnchanged
                && this.hasFormDataChangedSinceFetched()
            ) {
                this.isSubmittedFormDataUnchanged = false;
                this.submitted = false;
            }
        },

        async save() {
            const {
                isKmnBrandedOptInEnabled,
                isFormResubmittable,
                isValidForm,
                localSmsAccount,
                formData: {
                    generalDetails,
                    usageDetails,
                    contactInformation,
                    companyAddress,
                },
            } = this;

            this.submitted = true;
            this.hasValidationError = false;

            if (isFormResubmittable && !this.hasFormDataChangedSinceFetched()) {
                this.isSubmittedFormDataUnchanged = true;

                return;
            }

            if (isValidForm) {
                this.loading = true;

                const payload = {
                    appId: localSmsAccount.appId,
                    businessAddr1: companyAddress.address1,
                    businessAddr2: companyAddress.address2,
                    businessContactEmail: contactInformation.emailAddress,
                    businessContactFirstName: generalDetails.firstName,
                    businessContactLastName: generalDetails.lastName,
                    businessContactPhone: toE164NorthAmericaPhoneFormat(contactInformation.phoneNumber),
                    city: companyAddress.locality,
                    companyName: generalDetails.companyName,
                    corporateWebsite: generalDetails.website,
                    country: companyAddress.country,
                    messageVolume: usageDetails.messageVolume.toString(),
                    optInType: usageDetails.optInType,
                    sampleMessage: usageDetails.sampleMessage,
                    source: KMN_VALIDATION_SOURCE,
                    state: companyAddress.region,
                    useCaseCategories: usageDetails.useCases,
                    useCaseSummary: usageDetails.useCaseSummary,
                    zip: companyAddress.postalCode,
                    optInImageUrls: isKmnBrandedOptInEnabled
                        ? usageDetails.optInImageUrl
                        : KEAP_PROVIDED_OPT_IN_IMAGE_URL,
                };

                try {
                    if (this.isFormResubmittable) {
                        await axios.put('/app/automatedSmsFunnel/v2/verification', payload);
                    } else await axios.post('/app/automatedSmsFunnel/v2/verification', payload);

                    switch (this.source) {
                    case 'Copy broadcast':
                        this.$emit('copy-broadcast');
                        break;
                    case 'Edit broadcast':
                        this.$emit('edit-broadcast');
                        break;
                    case 'Resume broadcast':
                        this.$emit('resume-broadcast');
                        break;
                    default:
                        await this.loadSmsAccount();
                        this.$emit('complete', this.localSmsAccount || {});
                        break;
                    }
                } catch (e) {
                    this.$error({
                        message: ERROR_MESSAGE,
                        showIcon: true,
                        closeLabel: CLOSE_LABEL,
                        bottom: true,
                    });
                }

                this.submitted = false;
                this.loading = false;
            } else {
                this.hasValidationError = true;
            }
        },
    },
};
</script>

<style lang="scss" scoped>

// This rule is to override a competing core CSS rule
.kmn-verification-form ::v-deep .modal-header h4 {
  font-size: $font-size-lg;
}

.title {
  font-size: $font-size-jumbo;
  font-weight: $font-weight-bold;
  line-height: $line-height-xl;
  margin-bottom: $spacing-200;
}

.description {
  font-size: $font-size-md;
  line-height: $line-height-md;
}

.heading {
  border-bottom: none;
  margin-bottom: $spacing-400;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;

}

.heading-title-only {
  margin-bottom: $spacing-200;
}

.section-header,
.section-subheader {
  font-size: $font-size-md;
  line-height: $line-height-lg;
  margin-bottom: $spacing-200;
}

.section-subheader {
  font-weight: $font-weight-semibold;
}

.multiple-input-fields {
  display: flex;
  align-content: space-between;
  width: 100%;
}

.sample-message {
  --textarea-min-height: #{px-to-rem(50)};
  --input-margin-bottom: #{$spacing-050};
}

.sample-message-footer {
  font-size: $font-size-xs;
  line-height: $line-height-sm;
  color: $color-ink-800;
  margin: $spacing-100 0 $spacing-300;
  display: flex;
  justify-content: space-between;
}

.input-group {
  align-items: flex-start;
}

.input-field.company-website{
  margin-bottom: $spacing-300;
}

.opt-in-image-url-input{
  margin-bottom: $spacing-300;
}

section {
  margin-bottom: $spacing-300;

  &.separator {
    border-bottom: solid px-to-rem(1) $color-gray-200;
  }

  &:last-of-type {
    border-bottom: 0;
    margin-bottom: $spacing-100;
  }
}

.controls {
  display: flex;
  align-items: flex-start;
  margin-bottom: $spacing-100;
}

.finish-setup {
  min-width: fit-content;
  @include margin-end($spacing-200);
  &.loading {
    display: block !important;
  }
}

.footer-count {
  display: inline-block;
}

.verification-status-info {
  margin-bottom: $spacing-300;
  text-align: left;
  width: 100%;
  span {
    line-height: $line-height-md;
    font-size: $font-size-sm;
  }
}

.verification-status-alert,
.prohibited-content-alert {
  margin-bottom: $spacing-300;
  text-align: left;
  width: 100%;
  span {
    line-height: $line-height-md;
    font-size: $font-size-sm;
  }
}

.prohibited-content-alert {
  ul {
    margin: $spacing-100 0;
  }

  li {
    line-height: $line-height-md;
    font-size: $font-size-sm;
  }
}

.tooltip-subsection-header {
  font-size: $font-size-md;
  line-height: $line-height-md;
  margin-top: $spacing-200;
  margin-bottom: $spacing-200;
  display: flex;
  align-items: center;
  > .tooltip-icon {
    @include margin-end($spacing-050);
    --icon-size: #{px-to-rem(18)};
  }
}

.approved-example {
  color: $color-green;
}

.rejected-example {
  color: $color-red-800;
}

.usage-tooltip {
  padding: $spacing-200;
  .tooltip-example-subheader{
    margin-bottom: $spacing-200;
  }
  .section-header{
    font-weight: bold;
  }
}

.example-use-message,
.sample-message {
  --textarea-min-height: #{px-to-rem(50)};
  --input-margin-bottom: #{$spacing-050};
}

.popover-text {
  font-size: $font-size-md;
  --popover-width: none;
  margin-bottom: $spacing-200;
}

.character-count {
  float: right;
}

.uses-help {
  color: $color-ink-800;
  font-size: $font-size-sm;
}

.website-help {
  color: $color-ink-800;
  font-size: $font-size-xs;
  line-height: #{px-to-rem(16)};
  margin-bottom: $spacing-300;
}

.prohibited-types {
  list-style-type: disc;
  list-style-position: inside;
}

.controls {
  > *:not(:last-child) {
    margin-right: $spacing-100;
  }
}

.rejection-reason-container  {
  font-size: $font-size-sm;
  margin-top: $spacing-100;
}

.rejection-reason-value {
  font-weight: $font-weight-bold;
}

.number-opt-in-value {
  --input-padding-top: 0;
  --input-padding-bottom: 0;
}

.error-container {
    position: relative;
    width: 100%;
    margin-bottom: $spacing-400;
}

.external-link {
    --icon-size: #{px-to-rem(13.33)};
}

.opt-in-image-url-popover-item {
    margin: $spacing-200 0;
}
</style>
